import React, { useEffect, useState } from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import { getGuaranteeWeighingData } from '../../services/GuaranteesService';

interface MonetizationWeighingTabProps {
  guaranteeId: string;
  guaranteeType: 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement';
}

const MonetizationWeighingTab: React.FC<MonetizationWeighingTabProps> = ({ guaranteeId, guaranteeType }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchWeighingData();
  }, [guaranteeId, guaranteeType]);

  const fetchWeighingData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const type = guaranteeType === 'loan' ? 'loans' : guaranteeType === 'check' ? 'checks' : guaranteeType === 'promissory_note' ? 'promissory-notes' : 'commercial-endorsements';
      const response = await getGuaranteeWeighingData(type, guaranteeId);
      
      if (response.success && 'data' in response) {
        setData(response.data);
      } else {
        setError('Error al cargar los datos de monetización y ponderación');
      }
    } catch (err) {
      console.error('Error fetching weighing data:', err);
      setError('Error al cargar los datos de monetización y ponderación');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return date.toLocaleDateString('es-AR');
  };

  const InfoField = ({ label, value }: { label: string; value: string | number }) => (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'baseline' }}>
      <P type='3' variant='neutral' weight='bold' withMargin={false}>
        {label}:
      </P>
      <P type='3' variant='neutral' withMargin={false}>
        {value || '-'}
      </P>
    </div>
  );

  if (loading) {
    return (
      <Card margin={{ top: '4' }}>
        <TableSkeleton rows={4} columns={4} />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card display='flex' justifyContent='center' alignItems='center' height='200px'>
        <P type="1" weight="regular" modifier="weak">{error || 'No hay datos disponibles'}</P>
      </Card>
    );
  }

  const renderExecutionDataLoan = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Datos de ejecucion
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px' }}>
        <InfoField label="Fecha de monetizacion" value={formatDate(data.monetization_date)} />
        <InfoField label="Ejecutado" value={data.is_executed === true || data.is_executed === 'true' ? 'Sí' : 'No'} />
      </div>
    </>
  );

  const renderExecutionDataCheck = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Datos de ejecucion
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px' }}>
        <InfoField label="Fecha de monetizacion" value={formatDate(data.monetization_date)} />
        <InfoField label="Codigo CVBA" value={data.monetization_cvba_code} />
        <InfoField label="Monto total en pesos" value={data.monetization_amount} />
        <InfoField label="IVA" value={data.iva} />
        <InfoField label="Tasa de descuento" value={data.monetization_discount_rate} />
        <InfoField label="Intereses" value={data.monetization_interest} />
        <InfoField label="Fecha de liquidacion" value={formatDate(data.settlement_date)} />
        <InfoField label="Plazo de liquidacion" value={data.clearing} />
        <InfoField label="Alyc compradora" value={data.buyer_alyc} />
        <InfoField label="Alyc Vendedora" value={data.seller_alyc} />
        <InfoField label="Ejecutado" value={data.is_executed === true || data.is_executed === 'true' ? 'Sí' : 'No'} />
      </div>
    </>
  );

  const renderExecutionDataPromissoryNote = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Datos de ejecucion
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px' }}>
        <InfoField label="Fecha de monetizacion" value={formatDate(data.monetization_date)} />
        <InfoField label="Codigo CVBA" value={data.monetization_cvba_code} />
        <InfoField label="IVA" value={data.iva} />
        <InfoField label="Tasa de descuento" value={data.monetization_discount_rate} />
        <InfoField label="Intereses" value={data.monetization_interest} />
        <InfoField label="Fecha de liquidacion" value={formatDate(data.settlement_date)} />
        <InfoField label="Plazo de liquidacion" value={data.clearing} />
        <InfoField label="Alyc compradora" value={data.buyer_alyc} />
        <InfoField label="Alyc Vendedora" value={data.seller_alyc} />
        <InfoField label="Ejecutado" value={data.is_executed === true || data.is_executed === 'true' ? 'Sí' : 'No'} />
      </div>
    </>
  );

  const renderWeighingCheckLoan = () => (
    <>
        <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Ponderacion
        </H>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '16px' }}>
            <InfoField label="Porcentaje" value={data.weighting_percentage} />
            <InfoField label="Importe" value={data.weighting_amount} />
            <InfoField label="Categoria" value={data.category} />
            <InfoField label="Sin certificado Pyme" value="-" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: '16px', marginBottom: '32px' }}>
            <InfoField label="Ponderador aplicable" value={data.applicable_weigher} />
            <InfoField label="Comprobante contable de monetizacion" value={data.accounting_voucher || '-'} />
        </div>
    </>
  );

  const renderWeighingPromissoryNote = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Ponderacion
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '16px' }}>
        <InfoField label="Porcentaje" value={data.weighting_percentage} />
        <InfoField label="Importe" value={data.weighting_amount} />
        <InfoField label="Categoria" value={data.category} />
        <InfoField label="Sin certificado Pyme" value="-" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: '16px', marginBottom: '32px' }}>
            <InfoField label="Ponderador aplicable" value={data.applicable_weigher} />
        </div>
    </>
  );

  const renderExecutionDataCommercialEndorsement = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Datos de ejecucion
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px' }}>
        <InfoField label="Fecha de monetizacion" value={formatDate(data.monetization_date)} />
        <InfoField label="Codigo CVBA" value={data.monetization_cvba_code} />
        <InfoField label="Monto total en pesos" value={data.monetization_amount} />
        <InfoField label="IVA" value={data.iva} />
        <InfoField label="Tasa de descuento" value={data.monetization_discount_rate} />
        <InfoField label="Intereses" value={data.monetization_interest} />
        <InfoField label="Fecha de liquidacion" value={formatDate(data.settlement_date)} />
        <InfoField label="Plazo de liquidacion" value={data.clearing} />
        <InfoField label="Alyc compradora" value={data.buyer_alyc} />
        <InfoField label="Alyc Vendedora" value={data.seller_alyc} />
        <InfoField label="Ejecutado" value={data.is_executed === true || data.is_executed === 'true' ? 'Sí' : 'No'} />
      </div>
    </>
  );

  const renderWeighingCommercialEndorsement = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Ponderacion
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '16px' }}>
        <InfoField label="Porcentaje" value={data.weighting_percentage} />
        <InfoField label="Importe" value={data.weighting_amount} />
        <InfoField label="Categoria" value={data.category} />
        <InfoField label="Sin certificado Pyme" value="-" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <InfoField label="Ponderador aplicable" value={data.applicable_weigher} />
      </div>
    </>
  );

  return (
    <Card>
      {guaranteeType === 'loan' && renderExecutionDataLoan()}
      {guaranteeType === 'check' && renderExecutionDataCheck()}
      {guaranteeType === 'promissory_note' && renderExecutionDataPromissoryNote()}
      {guaranteeType === 'commercial_endorsement' && renderExecutionDataCommercialEndorsement()}
      
      {(guaranteeType === 'loan' || guaranteeType === 'check') && renderWeighingCheckLoan()}
      {guaranteeType === 'promissory_note' && renderWeighingPromissoryNote()}
      {guaranteeType === 'commercial_endorsement' && renderWeighingCommercialEndorsement()}
    </Card>
  );
};

export default MonetizationWeighingTab;
